<template>
  <div class="FinancesInvoicesCompanyPeriodsTable finances-invoices-table">
    <PanelHeader
      title="Employees"
      :isOpen="show"
      :onToggleButtonClick="togglePanel"
      titleClassName="col-md-8"
      buttonsWrapperClassName="col-md-4"
    >
      <AddItemButton :onClick="addItem" />
    </PanelHeader>
    <b-collapse class="mx-3" style="position: relative;" v-model="show">
      <b-table :fields="fields" :items="items" borderless>
        <template #head(commission)="{ label }">
          <span class="commission-header-text">Commission</span>
        </template>
        <template #cell(user)="{ item, index }">
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="item.user ? item.user.label : null">
            <multiselect
              :class="{ 'multiselect__single-line': true, 'is-invalid': isEmployeeFieldInvalid(item) || getBackendError(index, 'user_id'), 'is-valid': shouldShowGreenBorder(index, 'user') }"
              v-model="item.user"
              @input="dataTouched(index, 'user'); clearBackendError(index, 'user_id')"
              placeholder="Employee"
              :options="getUsersWithTrashedForSelect"
              track-by="id"
              label="label"
              return="id"
              :showLabels="false"
              allow-empty
            ></multiselect>
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'user_id')"
              :show-frontend-error="isEmployeeFieldInvalid(item)"
              frontend-message="Field is required"
            />
          </div>
        </template>
        <template #cell(period_cost)="{ item, index }">
          <div>
            <b-form-input
              type="number"
              v-model="item.period_cost"
              :class="{ 'is-invalid': isPeriodCostFieldInvalid(item) || getBackendError(index, 'period_cost'), 'is-valid': shouldShowGreenBorder(index, 'period_cost') }"
              @input="dataTouched(index, 'period_cost'); clearBackendError(index, 'period_cost')"
              min="1"
              no-wheel
            />
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'period_cost')"
              :show-frontend-error="isPeriodCostFieldInvalid(item)"
              frontend-message="Field is required"
            />
          </div>
        </template>
        <template #cell(hours_amount)="{ item, index }">
          <div>
            <b-form-input
              type="number"
              step="0.01"
              no-wheel
              v-model="item.hours_amount"
              :class="{ 'is-invalid': isHoursFieldInvalid(item) || getBackendError(index, 'hours_amount'), 'is-valid': shouldShowGreenBorder(index, 'hours_amount') }"
              @input="dataTouched(index, 'hours_amount'); clearBackendError(index, 'hours_amount')"
            />
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'hours_amount')"
              :show-frontend-error="isHoursFieldInvalid(item)"
              frontend-message="Field is required"
            />
            <span
              v-if="item.hoursCalculating || hoursTooltip(item)"
              class="cursor-pointer hours-info"
              v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
              :title="hoursTooltip(item)"
            >
              <div
                v-if="item.hoursCalculating"
                class="hours-loader spinner-border text-secondary"
                role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <font-awesome-icon
                v-else-if="hoursTooltip(item)"
                icon="info"
                class="hours-info-label"
              />
            </span>
          </div>
        </template>
        <template #cell(calculateHours)="{ item, index }">
          <b-form-checkbox
            type="checkbox"
            size="lg"
            v-model="item.calculateHours"
            @input="dataTouched(index, 'auto_hours_amount', $event)"
            :value="true"
            title="Auto calculate hours from worklogs and v&s google doc"
          />
        </template>
        <template #head(hours_amount)="data">
          {{ data.label }}
          <span class="cursor-pointer" v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title=data.field.tooltip>
            <font-awesome-icon icon="info" style="font-size: 0.7rem; margin-left:5px;"/>
          </span>
        </template>
        <template #cell(date_from)="{ item, index }">
          <b-form-group
            style="position: relative;">
            <!-- TODO NEED UPDATE VUE BOOTSTRAP AND REMOVE CURRENT DATAPICKER, INPUT -->
            <b-form-input
              style="position: absolute;"
              :value="convertDataToString(item.date_from)"
              :class="{ 'is-invalid': isDateFromFieldInvalid(item) || getBackendError(index, 'date_from'), 'is-valid': shouldShowGreenBorder(index, 'date_from') }"
            />
            <Datepicker
              :class="{ 'small-paddings-input': true, 'is-invalid': isDateFromFieldInvalid(item) || getBackendError(index, 'date_from'), 'is-valid': shouldShowGreenBorder(index, 'date_from') }"
              :bootstrap-styling="true"
              :value="_toIsoStringTime(item.date_from)"
              @input="item.date_from = _toLocalTime($event); dataTouched(index, 'date_from'); clearRelatedDateErrors(index, 'date_from')"
              format="yyyy-MM-dd"
            />
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'date_from')"
              :show-frontend-error="isDateFromFieldInvalid(item)"
              frontend-message="Field is required"
            />
          </b-form-group>
        </template>
        <template #cell(date_to)="{ item, index }">
          <b-form-group
            style="position: relative;">
            <!-- TODO NEED UPDATE VUE BOOTSTRAP AND REMOVE CURRENT DATAPICKER, INPUT -->
            <b-form-input
              style="position: absolute;"
              :value="convertDataToString(item.date_to)"
              :class="{ 'is-invalid': isDateToFieldInvalid(item) || getBackendError(index, 'date_to'), 'is-valid': shouldShowGreenBorder(index, 'date_to') }"
            />
            <Datepicker
              :class="{ 'small-paddings-input': true, 'is-invalid': isDateToFieldInvalid(item) || getBackendError(index, 'date_to'), 'is-valid': shouldShowGreenBorder(index, 'date_to') }"
              :bootstrap-styling="true"
              :value="_toIsoStringTime(item.date_to)"
              @input="item.date_to = _toLocalTime($event); dataTouched(index, 'date_to'); clearRelatedDateErrors(index, 'date_to')"
              format="yyyy-MM-dd"
            />
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'date_to')"
              :show-frontend-error="isDateToFieldInvalid(item)"
              frontend-message="Field is required"
            />
          </b-form-group>
        </template>
        <template #cell(commission)="{ item, index }">
          <b-form-input
            type="number"
            no-wheel
            v-model="item.commission"
            @input="dataTouched(index, 'commission')"
          />
        </template>
        <template #cell(actions)="data">
         <b-button variant="danger" size="sm w-100" @click.prevent="removeItem(data)">
           <font-awesome-icon icon="trash" />
         </b-button>
        </template>
      </b-table>
    </b-collapse>

    <PanelDelimiter />
  </div>
</template>

<script>
import FormItemsMixin from '../../mixins/FormItemsMixin'
import PanelHeader from '@components/invoices/form/PanelHeader.vue'
import AddItemButton from '@components/modals/FinanceInvoicesForm/components/AddItemButton.vue'
import PanelDelimiter from '@components/modals/FinanceInvoicesForm/components/PanelDelimiter.vue'
import FieldErrorMessage from '@components/forms/FieldErrorMessage.vue'

export default {
  components: { PanelDelimiter, AddItemButton, PanelHeader, FieldErrorMessage },
  mixins: [FormItemsMixin],
  props: {
    columnHeaderClass: {
      type: String,
      default: ''
    },
    validation: {
      type: Object,
      default: null
    },
    formSubmissionAttempted: {
      type: Boolean,
      default: false
    },
    backendErrors: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    formSubmissionAttempted (newValue) {
      // Track field errors when form submission is attempted
      if (newValue) {
        this.trackFieldErrors()
      }
    },
    backendErrors: {
      deep: true,
      handler (newErrors) {
        // Track backend errors
        if (newErrors && Object.keys(newErrors).length > 0) {
          this.trackFieldErrors()
        }
      }
    }
  },
  data () {
    return {
      fields: [
        { key: 'user', label: 'Employee', class: 'employee-column ' + this.columnHeaderClass },
        { key: 'period_cost', label: 'Cost', class: 'cost-column minor-paddings ' + this.columnHeaderClass },
        { key: 'hours_amount', label: 'Hours', class: 'hours-column minor-paddings ' + this.columnHeaderClass, tooltip: 'All paid hours (work + vacation + sick leave)' },
        { key: 'calculateHours', label: 'Auto', class: 'calculate-hours-column ' + this.columnHeaderClass },
        { key: 'date_from', label: 'Date from', class: 'date-from-column date-column ' + this.columnHeaderClass },
        { key: 'date_to', label: 'Date to', class: 'date-to-column date-column ' + this.columnHeaderClass },
        { key: 'commission', label: 'Commission', class: 'commission-column ' + this.columnHeaderClass },
        { key: 'actions', label: '', class: 'actions-column' }
      ],
      items: [],
      fieldsWithPreviousErrors: new Map(), // Track fields with errors by item index and field name
      defaultItem: {
        'user': null,
        'period_cost': 0,
        'hours_amount': 0,
        'date_from': null,
        'date_to': null,
        'commission': null,
        'calculateHours': true,
        'hoursCalculating': false
      }
    }
  },
  methods: {
    clearBackendError (itemIndex, fieldName) {
      const errorKey = `periods.${itemIndex}.${fieldName}`
      if (this.backendErrors[errorKey]) {
        // Create a copy to trigger reactivity
        const updatedErrors = { ...this.backendErrors }
        delete updatedErrors[errorKey]
        this.$emit('updateBackendErrors', updatedErrors)
      }
    },
    clearRelatedDateErrors (itemIndex, changedField) {
      // When date_from or date_to changes, clear both fields' backend errors since they're related
      const relatedDateFields = ['date_from', 'date_to']
      let hasChanges = false
      const updatedErrors = { ...this.backendErrors }

      relatedDateFields.forEach(fieldName => {
        const errorKey = `periods.${itemIndex}.${fieldName}`
        if (this.backendErrors[errorKey]) {
          delete updatedErrors[errorKey]
          hasChanges = true
        }
      })

      if (hasChanges) {
        this.$emit('updateBackendErrors', updatedErrors)
      }
    },
    getBackendError (itemIndex, fieldName) {
      const errorKey = `periods.${itemIndex}.${fieldName}`
      const error = this.backendErrors[errorKey]
      return error ? (Array.isArray(error) ? error[0] : error) : null
    },
    isEmployeeFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && !item.user
    },
    isEmployeeFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'user')
    },
    isPeriodCostFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.period_cost || item.period_cost <= 0)
    },
    isPeriodCostFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'period_cost')
    },
    isHoursFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.hours_amount || item.hours_amount <= 0)
    },
    isHoursFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'hours_amount')
    },
    isDateFromFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && !item.date_from
    },
    isDateFromFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'date_from')
    },
    isDateToFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && !item.date_to
    },
    isDateToFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'date_to')
    },
    shouldShowGreenBorder (itemIndex, fieldName) {
      // Only show green border if the field had errors before and is now valid
      const hasError = this.isFieldCurrentlyInvalid(itemIndex, fieldName)

      const errorKey = `${itemIndex}_${fieldName}`
      const hadPreviousError = this.fieldsWithPreviousErrors.has(errorKey)
      const isDirty = this.validation?.$dirty

      return hadPreviousError && !hasError && isDirty && this.formSubmissionAttempted
    },
    isFieldCurrentlyInvalid (itemIndex, fieldName) {
      const item = this.items[itemIndex]
      if (!item) return false

      const backendError = this.getBackendError(itemIndex, fieldName)

      switch (fieldName) {
        case 'user':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && !item.user) || backendError
        case 'period_cost':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.period_cost || item.period_cost <= 0)) || backendError
        case 'hours_amount':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.hours_amount || item.hours_amount <= 0)) || backendError
        case 'date_from':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && !item.date_from) || backendError
        case 'date_to':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && !item.date_to) || backendError
        default:
          return false
      }
    },
    trackFieldErrors () {
      // Track which fields currently have errors for each item
      const requiredFields = ['user', 'period_cost', 'hours_amount', 'date_from', 'date_to']

      this.items.forEach((item, index) => {
        requiredFields.forEach(fieldName => {
          const hasError = this.isFieldCurrentlyInvalid(index, fieldName)

          const errorKey = `${index}_${fieldName}`
          if (hasError) {
            this.fieldsWithPreviousErrors.set(errorKey, true)
          }
        })
      })
    },
    removeItem (data) {
      // Clear error tracking for removed item
      const requiredFields = ['user', 'period_cost', 'hours_amount', 'date_from', 'date_to']
      requiredFields.forEach(fieldName => {
        const errorKey = `${data.index}_${fieldName}`
        this.fieldsWithPreviousErrors.delete(errorKey)
      })

      // Re-index error tracking for remaining items
      const newErrorTracking = new Map()
      this.fieldsWithPreviousErrors.forEach((value, key) => {
        const [itemIndex, fieldName] = key.split('_')
        const indexNum = parseInt(itemIndex)
        if (indexNum > data.index) {
          // Shift index down for items after the removed one
          const newKey = `${indexNum - 1}_${fieldName}`
          newErrorTracking.set(newKey, value)
        } else if (indexNum < data.index) {
          // Keep existing index for items before the removed one
          newErrorTracking.set(key, value)
        }
        // Skip the removed item (indexNum === data.index)
      })
      this.fieldsWithPreviousErrors = newErrorTracking

      // Call parent removeItem method
      this.items = this.items.filter((item, index) => index !== data.index)
      this.triggerInputEmit()
    },
    clearErrorTracking () {
      // Clear all error tracking (called when parent form is reset)
      this.fieldsWithPreviousErrors.clear()
    }
  }
}
</script>

<style lang="scss" scoped>
// Validation styling for Datepicker component
:global(.vdp-datepicker.is-invalid input) {
  border-color: #DC3545 !important;
  background-image: none !important;
  padding-right: 12px !important;
}

:global(.vdp-datepicker.is-valid input) {
  border-color: #3FA52F !important;
  background-image: none !important;
  padding-right: 12px !important;
}
</style>
